import { useState, useEffect } from 'react';
import {
  upsertYearRate, getAllFXRates, normaliseCurrency,
} from '../../../services/fxRateService';
import { getAllCountries } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import Button from '../../shared/UI/Button';

// Worked example used in the orientation hint. GHS is the fund's most common
// non-USD reporting currency and its year-end rate is far from parity, which
// makes the direction unmistakable.
const EXAMPLE = 'GHS ≈ 0.083';

/**
 * Add or edit one year's year-end rate for one currency.
 *
 * `entry` is a flattened { currency, year, rate, status, note } row from the
 * list, or null when adding. Editing a CLOSED year requires a correction
 * reason — enforced in fxRateService, surfaced here before the user submits.
 */
export default function FXRateForm({ entry, onSaved, onCancel }) {
  const { user } = useAuth();
  const isEdit = !!entry;
  const wasClosed = entry?.status === 'closed';

  const [form, setForm] = useState({
    currency: entry?.currency || '',
    year: entry?.year ?? '',
    rate: entry?.rate ?? '',
    status: entry?.status || 'provisional',
    note: entry?.note || '',
    reason: '',
  });
  const [suggestions, setSuggestions] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Suggestions only — the registry is self-authoritative and accepts any valid
  // 3-letter ISO code, so this never constrains what can be entered. Sourced
  // from the countries registry and from currencies already carrying rates;
  // deliberately NOT from a hardcoded array (see OI-018).
  useEffect(() => {
    Promise.all([getAllCountries(), getAllFXRates()])
      .then(([countries, fx]) => {
        const codes = new Set();
        countries.forEach(c => { if (c.currency_code) codes.add(c.currency_code); });
        fx.forEach(d => { if (d.currency_code) codes.add(d.currency_code); });
        setSuggestions([...codes].sort());
      })
      .catch(() => setSuggestions([]));
  }, []);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!String(form.currency).trim()) e.currency = 'Currency is required';
    if (!String(form.year).trim()) e.year = 'Year is required';
    if (String(form.rate).trim() === '') e.rate = 'Rate is required';
    if (wasClosed && !String(form.reason).trim()) {
      e.reason = 'A reason is required to correct a closed year';
    }
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    setResult(null);
    try {
      const res = await upsertYearRate({
        currency: form.currency,
        year: form.year,
        rate: form.rate,
        status: form.status,
        source: 'manual',
        note: form.note,
        // Editing a year that is already closed is, by definition, a
        // correction. The service refuses it without a reason.
        correction: wasClosed,
        reason: form.reason,
        performedBy: user.uid,
      });
      setResult(res);
      // Hold on screen briefly when there is a warning to read.
      setTimeout(() => onSaved(), res.warning ? 3200 : 1400);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  const currencyLabel = normaliseCurrency(form.currency) || 'this currency';

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {result && (
        <div className={`alert ${result.warning ? 'alert-warning' : 'alert-success'}`}>
          Saved {result.currency} {result.year} at {result.rate} ({result.status}).
          {result.warning && <div style={{ marginTop: 6 }}>⚠ {result.warning}</div>}
        </div>
      )}

      {wasClosed && (
        <div className="alert alert-warning">
          <strong>{entry.currency} {entry.year} is a closed year.</strong> Its rate is
          locked. Saving a change records a correction: the current value is archived
          to the document&apos;s history and the version is incremented. A reason is required.
        </div>
      )}

      <div className="form-grid mb-4">
        <FormField label="Currency" required error={errors.currency}
          hint="Any 3-letter ISO code. Known codes are suggested, but not required.">
          <input
            className={`form-input ${errors.currency ? 'error' : ''}`}
            list="fx-currency-suggestions"
            value={form.currency}
            maxLength={3}
            placeholder="e.g. GHS"
            readOnly={isEdit}
            style={isEdit ? { background: 'var(--color-border-light)', cursor: 'not-allowed' } : {}}
            onChange={e => set('currency', e.target.value.toUpperCase())}
          />
        </FormField>
        <datalist id="fx-currency-suggestions">
          {suggestions.map(c => <option key={c} value={c} />)}
        </datalist>

        <FormField label="Year" required error={errors.year}
          hint="Calendar year. All fiscal years end in December, so this is that year's December close.">
          <input
            type="number"
            className={`form-input ${errors.year ? 'error' : ''}`}
            value={form.year}
            placeholder="e.g. 2023"
            readOnly={isEdit}
            style={isEdit ? { background: 'var(--color-border-light)', cursor: 'not-allowed' } : {}}
            onChange={e => set('year', e.target.value)}
          />
        </FormField>

        <FormField
          label="Year-end rate"
          required
          error={errors.rate}
          hint={`Enter USD per 1 unit of ${currencyLabel} — e.g. ${EXAMPLE}. This is the INVERSE of the usual quote: if ${currencyLabel} trades at 12 to the dollar, enter 0.083.`}
        >
          <input
            type="number"
            step="any"
            className={`form-input ${errors.rate ? 'error' : ''}`}
            value={form.rate}
            placeholder="e.g. 0.083"
            onChange={e => set('rate', e.target.value)}
          />
        </FormField>

        <SelectField
          label="Status"
          value={form.status}
          onChange={e => set('status', e.target.value)}
          placeholder=""
          hint="Provisional years can be updated freely. Closed years lock and require a reason to change."
          options={[
            { value: 'provisional', label: 'Provisional — year not yet closed' },
            { value: 'closed', label: 'Closed — final, locks the rate' },
          ]}
        />

        <FormField label="Note" hint="Optional. Source of the rate, or anything a later reader needs.">
          <input className="form-input" value={form.note}
            onChange={e => set('note', e.target.value)} />
        </FormField>

        {wasClosed && (
          <FormField label="Reason for correction" required error={errors.reason}
            hint="Recorded in the document history and the audit log.">
            <input className={`form-input ${errors.reason ? 'error' : ''}`}
              value={form.reason} placeholder="e.g. Central bank restated the December close"
              onChange={e => set('reason', e.target.value)} />
          </FormField>
        )}
      </div>

      <div className="flex-between">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>
          {isEdit ? 'Save Rate' : 'Add Rate'}
        </Button>
      </div>
    </form>
  );
}
